import React, { Component, createRef } from 'react';
import ReactDOM from 'react-dom';
import { urlMap, getAuthHeaders } from '../../../utils';

/**
 * ClipboardNote
 *
 * A draggable sticky note rendered via portal so it floats outside
 * the content column. Persists text at project level.
 *
 * Props:
 *  - projectId: string
 */
export default class ClipboardNote extends Component {
  state = {
    text: '',
    open: false,
    loaded: false,
    saving: false,
    // position (relative to viewport)
    x: null,
    y: null,
    minimized: false,
  };

  saveTimer = null;
  dragging = false;
  dragOffset = { x: 0, y: 0 };
  noteRef = createRef();

  componentDidMount() {
    if (this.props.projectId) this.load();
  }

  componentDidUpdate(prev) {
    if (prev.projectId !== this.props.projectId) this.load();
  }

  componentWillUnmount() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    document.removeEventListener('mousemove', this.onDragMove);
    document.removeEventListener('mouseup', this.onDragEnd);
  }

  /* ── data ── */

  load = async () => {
    const { projectId } = this.props;
    if (!projectId) return;
    try {
      const res = await fetch(urlMap.getProjectClipboard(projectId), {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) return;
      const { text } = await res.json();
      this.setState({ text: text || '', loaded: true });
    } catch {
      this.setState({ loaded: true });
    }
  };

  save = async () => {
    const { projectId } = this.props;
    if (!projectId) return;
    this.setState({ saving: true });
    try {
      await fetch(urlMap.getProjectClipboard(projectId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ text: this.state.text }),
      });
    } catch { /* silent */ }
    this.setState({ saving: false });
  };

  handleChange = (e) => {
    this.setState({ text: e.target.value });
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(this.save, 1200);
  };

  /* ── open / close ── */

  toggle = () => {
    this.setState((s) => {
      if (!s.open && s.x === null) {
        // initial position: top-right area
        return { open: true, x: window.innerWidth - 340, y: 80, minimized: false };
      }
      return { open: !s.open, minimized: false };
    });
  };

  close = () => this.setState({ open: false });

  toggleMinimize = () => this.setState((s) => ({ minimized: !s.minimized }));

  /* ── drag ── */

  onDragStart = (e) => {
    // only drag from the header bar
    if (e.button !== 0) return;
    e.preventDefault();
    this.dragging = true;
    const rect = this.noteRef.current.getBoundingClientRect();
    this.dragOffset = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    document.addEventListener('mousemove', this.onDragMove);
    document.addEventListener('mouseup', this.onDragEnd);
  };

  onDragMove = (e) => {
    if (!this.dragging) return;
    const x = Math.max(0, Math.min(e.clientX - this.dragOffset.x, window.innerWidth - 60));
    const y = Math.max(0, Math.min(e.clientY - this.dragOffset.y, window.innerHeight - 40));
    this.setState({ x, y });
  };

  onDragEnd = () => {
    this.dragging = false;
    document.removeEventListener('mousemove', this.onDragMove);
    document.removeEventListener('mouseup', this.onDragEnd);
  };

  /* ── render ── */

  renderNote() {
    const { text, saving, x, y, minimized } = this.state;

    return ReactDOM.createPortal(
      <div
        ref={this.noteRef}
        className={`clipboard-sticky${minimized ? ' clipboard-sticky--minimized' : ''}`}
        style={{ left: x, top: y }}
      >
        <div
          className="clipboard-sticky-header"
          onMouseDown={this.onDragStart}
        >
          <span className="clipboard-sticky-title">📋 Notes</span>
          {saving && <span className="clipboard-sticky-saving">saving…</span>}
          <div className="clipboard-sticky-actions">
            <button
              type="button"
              className="clipboard-sticky-btn"
              onClick={this.toggleMinimize}
              title={minimized ? 'Expand' : 'Minimize'}
            >
              {minimized ? '▢' : '—'}
            </button>
            <button
              type="button"
              className="clipboard-sticky-btn"
              onClick={this.close}
              title="Close"
            >
              ×
            </button>
          </div>
        </div>
        {!minimized && (
          <textarea
            className="clipboard-sticky-textarea"
            value={text}
            onChange={this.handleChange}
            placeholder="Jot down notes, references, or reminders…"
            rows={10}
          />
        )}
      </div>,
      document.body
    );
  }

  render() {
    const { open } = this.state;
    const { hideTrigger } = this.props;

    return (
      <>
        {!hideTrigger && (
          <button
            type="button"
            className={`btn subtle clipboard-sticky-trigger${open ? ' panel-active' : ''}`}
            onClick={this.toggle}
            title={open ? 'Hide clipboard' : 'Show clipboard'}
            aria-label={open ? 'Hide clipboard' : 'Show clipboard'}
          >
            📋 Clipboard
          </button>
        )}
        {open && this.renderNote()}
      </>
    );
  }
}
